import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import Radio from '@mui/material/Radio';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { APIContext } from '../../../Contexts/APIContext';
import DataContainer from '../../../Utilities/DataContainer';

interface IProps {
    id: string;
    selectedDateIndex: string;
    setSelectedDateIndex: (value: string) => void;
    readOnly: boolean;
    surveyType: number;
}

export default function SurvayDates({ id, selectedDateIndex, setSelectedDateIndex, readOnly, surveyType }: IProps) {
    const { get, post, showMessage, loading } = useContext(APIContext);
    const [surveyDates, setSurveyDates] = useState<string[]>([]);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [newSurveyDate, setNewSurveyDate] = useState<Dayjs | null>(null);

    const loadSurveyDates = (dateToSelect?: string) => {
        get('SurveyDays', `GetSurveyDates?id=${id}`, (value: string[]) => {
            setSurveyDates(value);
            setSelectedDateIndex(dateToSelect ?? value[0] ?? '');
        });
    };

    useEffect(() => {
        let cancelled = false;
        setSurveyDates([]);

        get('SurveyDays', `GetSurveyDates?id=${id}`, (value: string[]) => {
            if (cancelled) {
                return;
            }

            setSurveyDates(value);
            setSelectedDateIndex(value[0] ?? '');
        })

        return () => {
            cancelled = true;
        };
    }, [id]);

    const parseUKDate = (value: string): Dayjs => {
        const [day, month, year] = value.split('/').map(Number);
        return dayjs(new Date(year, month - 1, day));
    };

    const openAddDialog = () => {
        setNewSurveyDate(surveyDates.length > 0 ? parseUKDate(surveyDates[0]) : dayjs());
        setAddDialogOpen(true);
    };

    const isDuplicateDate = newSurveyDate != null && surveyDates.includes(newSurveyDate.format('DD/MM/YYYY'));

    const addSurveyDay = () => {
        if (newSurveyDate == null || !newSurveyDate.isValid() || isDuplicateDate || loading) {
            return;
        }

        post('SurveyDays', 'AddSurveyDay', {
            id,
            date: newSurveyDate.format('YYYY-MM-DD'),
            surveyType,
        }, (createdDate: string) => {
            showMessage('success', 'Survey day added successfully');
            setAddDialogOpen(false);
            loadSurveyDates(createdDate);
        });
    };

    function getDayOfWeekFromUKDate(ukDateStr: string): string {
        const [day, month, year] = ukDateStr.split("/").map(Number);

        // Create a Date object (note: months are 0-based in JS)
        const date = new Date(year, month - 1, day);

        // Array of weekday names
        const daysOfWeek = [
            "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
        ];

        // Get the day index and return the corresponding name
        return daysOfWeek[date.getDay()];
    }

    return (
        <Grid size={12} alignItems="flex-start">
            <DataContainer title={"Survey Dates"}>
                {surveyDates.map((day) => (
                    <Box key={day} sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <FormControlLabel
                            control={
                                <Radio
                                    checked={selectedDateIndex === day}
                                    onChange={() => setSelectedDateIndex(day)}
                                    disabled={!readOnly}
                                />
                            }
                            label={`${day} - ${getDayOfWeekFromUKDate(day)}`}
                        />
                    </Box>
                ))}
                {!readOnly && (
                    <Button variant="outlined" onClick={openAddDialog} sx={{ mt: 1 }}>
                        Add survey day
                    </Button>
                )}
            </DataContainer>
            <Dialog open={addDialogOpen} onClose={() => !loading && setAddDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Add survey day</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                        <DatePicker
                            label="Survey date"
                            value={newSurveyDate}
                            onChange={setNewSurveyDate}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: isDuplicateDate,
                                    helperText: isDuplicateDate ? 'That survey date already exists.' : undefined,
                                },
                            }}
                        />
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialogOpen(false)} disabled={loading}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={addSurveyDay}
                        disabled={newSurveyDate == null || !newSurveyDate.isValid() || isDuplicateDate || loading}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );

}
